import { AfterViewInit, Component, inject, OnDestroy } from "@angular/core";
import {MatDialog,MatDialogConfig } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { MemoizedSelectorWithProps, Store } from "@ngrx/store";
import { Observable, Subject, fromEvent } from "rxjs";
import { Injector } from "@angular/core";
import { debounceTime, take, takeUntil } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { UiService } from "../services/ui.service";
import { UserService } from "../services/user.service";
import { EventService } from "../services/event.service";
import { AuthService } from "../services/auth.service";
import { SettingsService } from "../services/settings.service";
import { AccountsService } from "../services/accounts.service";
import { FiltersService } from "../services/filters.service";
import { LogService } from "../services/log.service";
import { filterPageTypes } from "../../../globals/store/filters.data";
import { EventViewService } from "../services/event.view.service";
import { ProgramService } from "../services/program.service";
import { Session } from "globals/store/programs.data";
import publicHelpers from "globals/helpers/public-helpers";
import { LocationService } from "../services/location.service";
import { AssetsService } from "../services/assets.service";
import { InformablesService } from "../services/informables.service";
import { PerformersService } from "../services/performers.service";
import { ShareBriefsService } from "../services/share.briefs.service";
import { ShareService } from "../services/share.service";
import { sponsorDisplayTypes } from "../store/sponsors.era";
import { performerDisplayTypes } from "../store/performers.era";
import { ShareableItemTypes } from "globals/store/share.data";
import { BudgetService } from "../services/budget.service";
import { ResourceService } from "../services/resources.service";
import { ExhibitorsService } from "../services/exhibitors.service";
import { AttendeesService } from "../services/attendees.service";
import { SponsorsService } from "../services/sponsors.service";
import { WorkforceService } from "../services/workforce.service";
import { ContactsService } from "../services/contacts.service";
import { TaskService } from "../services/task.service";
import { EventPermissionsService } from "../services/event.permissions.service";
import { tableLightColors } from "globals/helpers/table-helpers.data";
import { LogisticsShareService } from "../services/share.logistics.service";
import { InputDialogComponent, InputDialogData, InputDialogReturnData } from "./wizards/input.dialog.component";
import { SharePublishService } from "../services/share.publish.service";
import { AppService } from "../services/app.service";
import { BackupService } from "../services/backup.service";
import { ShareOnsiteService } from "../services/share.onsite.service";
import { ActivityService } from "../services/activity.service";
import { jsonCopyObject } from "globals/helpers/helpers";
import { QuotesService } from "../services/quotes.service";

export class Services {
    static router: Router
    static dialog: MatDialog
    static programService: ProgramService

    static initialize(injector: Injector) {
      Services.router = injector.get(Router)
      Services.dialog = injector.get(MatDialog)
      Services.programService = injector.get(ProgramService)
    }
}

@Component({
    template: '',
    standalone: true
})
export class Wrapper<Child> implements OnDestroy, AfterViewInit {
  $destroy: Subject<boolean> = new Subject()
  public get dialog():  MatDialog { return Services.dialog }
  protected appService = inject(AppService);
  protected activityService = inject(ActivityService);
  protected attendeeService = inject(AttendeesService);
  protected accountService = inject(AccountsService);
  protected backupService = inject(BackupService);
  protected budgetService = inject(BudgetService);
  protected contactsService = inject(ContactsService);
  protected assetService = inject(AssetsService);
  protected filtersService = inject(FiltersService);
  protected informablesService = inject(InformablesService);
  protected uiService = inject(UiService);
  protected userService = inject(UserService);
  protected eventService = inject(EventService);
  protected settingsService = inject(SettingsService);
  protected programService = inject(ProgramService);
  protected performerService = inject(PerformersService);
  protected shareBriefsService = inject(ShareBriefsService);
  protected sharePublishService = inject(SharePublishService);
  protected shareOnsiteService = inject(ShareOnsiteService);
  protected sponsorService = inject(SponsorsService);
  protected resourceService = inject(ResourceService);
  protected shareService = inject(ShareService);
  protected logService = inject(LogService);
  protected locationService = inject(LocationService);
  protected logisticsShareService = inject(LogisticsShareService);
  // protected eventViewService = inject(EventViewService)
  protected exhibitorService = inject(ExhibitorsService);
  protected taskService = inject(TaskService);
  protected workforceService = inject(WorkforceService);
  protected permissionsService = inject(EventPermissionsService);
  protected quotesService = inject(QuotesService)
  protected http = inject(HttpClient);
  protected store = inject(Store);
  protected router = inject(Router);
  protected auth = inject(AuthService);
  filterPageTypes = filterPageTypes;

  ready = false
  now: number
  pushing = false
  mobileView: boolean
  helpers = publicHelpers
  shareStatusToColor = { 'shared': tableLightColors.green, 'never shared': tableLightColors.gray, 'unshared': tableLightColors.red }
  shareStatusToText = { 'shared': 'Shared', 'never shared': 'Never shared', 'unshared': 'Unshared' }

  sponsorDisplayTypes = sponsorDisplayTypes
  performerDisplayTypes = performerDisplayTypes
  shareTypes = ShareableItemTypes

  constructor() {
    this.setNow()
    setInterval(this.setNow.bind(this), 10000)
  }

  setNow() {
    this.now = new Date().getTime()
  }

  ngAfterViewInit() { setTimeout(() => this.ready = true, 10) } // minor delay needed for rendering, not sure if 10 ms is sufficient for most browsers
  ngOnDestroy() { this.$destroy.next(true); this.$destroy.complete() }

  listenUntilResult = <T>(obs: Observable<T>): Promise<T> => new Promise<T>(resolve => {
    const destroyer: Subject<boolean> = new Subject()
    this.subCallback(obs, result => {
      if (!result) return
      destroyer.next(true); destroyer.complete()
      resolve(result)
    }, destroyer)
  })

  openDialogGetResult = <T extends unknown, C = any>(component: any, config?: MatDialogConfig): Promise<T> => this.dialog.open(component, config).afterClosed().toPromise()

  openInputDialog = (color: string, title: string, header: string, placeholder: string, successLabel: string, currentValue: string = null, emptyOK = false, secondValue: string = null, secondPlaceholder: string = null): Promise<InputDialogReturnData> => {
    const data: InputDialogData = {
      title,
      placeholder,
      header,
      successLabel,
      currentValue,
      secondValue,
      secondPlaceholder,
      emptyOK,
      backgroundColor: color,
    }
    return this.openDialogGetResult<InputDialogReturnData>(InputDialogComponent, { width: '480px', disableClose: true, data })
  }
 
  subObservableWithDebounce = <T>(obs: Observable<T>, debounceMS: number, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    obs.pipe(debounceTime(debounceMS)).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  take1 = <T>(obs: Observable<T>): Promise<T> =>
    obs.pipe(take(1)).toPromise()

  subCallback = <T> (obs: Observable<T>, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  subProp = <T extends Child[K], K extends keyof Child> (obs: Observable<T>, prop: K, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => (this as unknown as Child)[prop] = jsonCopyObject(val))

  subPropMutable = <T extends Child[K], K extends keyof Child> (obs: Observable<T>, prop: K, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => (this as unknown as Child)[prop] = jsonCopyObject(val))

  // props refers to the type passed to the reselector as the extra prop input
  selectCallback = <T, P>(reselector, props: P, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
  this.store.select(reselector, props).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  // props refers to the type passed to the reselector as the extra prop input
  selectProp = <T extends Child[K], K extends keyof Child, P>(reselector: MemoizedSelectorWithProps<Store, P, T>, props: P, propOrCallback: K, destroySubject: Subject<boolean> = null) => 
    this.store.select(reselector, props).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => (this as unknown as Child)[propOrCallback] = jsonCopyObject(val)
  )

  trackByIndex(index) {
    return index
  }

  trackById<T extends { id?: string }>(_, item: T) {
    return item.id
  }

  trackBySelf<T>(_, item: T) {
    return item // in case of primitive, compares primitives.  Objects compare references
  }

  trackBySessionDescriptionHash(index, session: Session) {
    return this.programService.sessionDescriptionHashes[session.id] // hash is calculated with id + description, but needs to be on the way in, not dynamically (see program service).  There is a slight bug with this - when you click into the same session's title after editing its description, the cursor loses focus.    
  }
  
  

}
